import { StackHeaderProps } from '@react-navigation/stack';
import { theme } from 'aimo-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import LegalAddressTermsAuthScreen from 'screens/account/LegalAddressTermsAuthScreen';
import CityParkingActionScreen from 'screens/cityParking/CityParkingActionScreen';
import CityParkingApplyScreen from 'screens/cityParking/CityParkingApplyScreen';
import CityParkingHomeAddress from 'screens/cityParking/CityParkingHomeAddress';
import CityParkingMapsScreen from 'screens/cityParking/CityParkingMapsScreen';
import { BuyScreen, CompletedScreen } from 'screens/longTermParking';
import ItemUnavailableModalContent from 'screens/permit/ItemUnavailableModalContent';
import { useCityParkingStore } from 'stores/cityParkingStore';
import { useMapStore } from 'stores/mapStore';
import { MainWrapper } from 'styles/ModalStackContainerStyles';
import { CityParkingParamList } from 'types/navigation';
import { createStackNavigator } from 'utils/navigationHelpers';
import { getModalOptions, transitionOptions } from 'utils/navigationUtils';
import ModalStackHeader from './ModalStackHeader';
import StackHeader from './StackHeader';

const ModalHeader = (props: StackHeaderProps) => (
  <ModalStackHeader
    {...props}
    showHandle
    closeButton
    closeButtonType="secondary"
    leftAlignHeader
    style={{
      marginBottom: 20,
    }}
  />
);

const BuyScreenWithPadding = () => (
  <MainWrapper>
    <BuyScreen />
  </MainWrapper>
);

const DuplicatePermitWarning = () => (
  <MainWrapper>
    <ItemUnavailableModalContent contentType="duplicatePermitWarning" />
  </MainWrapper>
);

const CityStack = createStackNavigator<CityParkingParamList>();
const CityParkingNavigator: React.FC = () => {
  const modalOptions = useMemo(
    () => getModalOptions('50%', true, undefined, 200),
    []
  );
  const { t } = useTranslation();
  const selectedPmc = useCityParkingStore((state) => state.selectedPmc);
  return (
    <CityStack.Navigator
      screenOptions={{
        ...transitionOptions({ closeDuration: 800, openDuration: 600 }),
        header: StackHeader,
        cardOverlayEnabled: true,
        cardStyle: { backgroundColor: theme.colors.white },
      }}>
      <CityStack.Screen
        name="cityPmcs"
        component={CityParkingMapsScreen}
        listeners={{
          // This is needed to reset the map to the last selected coordinates when navigating back to the map
          focus: () => {
            if (!useCityParkingStore.getState().previousCoordinates) {
              useCityParkingStore
                .getState()
                .setPreviousCoordinates(
                  useMapStore.getState().regionCoordinates
                );
            }
            useCityParkingStore.getState().setSelectedPmc(null);
          },
          blur: () => {
            const prevCoords =
              useCityParkingStore.getState().previousCoordinates;
            if (prevCoords) {
              useMapStore.getState().setMapCoordinates(prevCoords);
            }
          },
        }}
        options={{
          headerShown: false,
        }}
      />
      <CityStack.Group
        screenOptions={{
          ...modalOptions.screenOptions,
          header: ModalHeader,
        }}>
        <CityStack.Screen
          name="viewScreen"
          component={CityParkingActionScreen}
          options={{
            title: selectedPmc?.name ?? '',
          }}
        />
        <CityStack.Screen
          name="duplicate"
          component={DuplicatePermitWarning}
          options={{
            title: selectedPmc?.name ?? '',
          }}
        />
        <CityStack.Screen
          name="homeAddress"
          component={CityParkingHomeAddress}
          options={{
            title: t('cityPermits.homeAddress'),
          }}
        />
      </CityStack.Group>
      <CityStack.Screen
        name="purchase"
        component={BuyScreenWithPadding}
        options={{
          title: t('cityPermits.purchase'),
        }}
      />

      <CityStack.Screen
        name="identification"
        component={LegalAddressTermsAuthScreen}
        options={{
          title: t('legalAddressAuth.identification'),
        }}
      />
      <CityStack.Screen
        name="apply"
        component={CityParkingApplyScreen}
        options={{ title: t('cityPermits.applicationTitle') }}
      />
      <CityStack.Screen
        name="completed"
        component={CompletedScreen}
        options={{
          headerShown: false,
          title: t('longTermParking.complete.thankYou'),
        }}
      />
    </CityStack.Navigator>
  );
};

export default CityParkingNavigator;
